import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, ReferenceLine } from 'recharts';

export const TrajectoryChart = React.memo(function TrajectoryChart({
  shotGroups,
  selectedShots = [],
  showAverage = true,
  distanceType = 'carry',
  simulationMode = false,
  simulatedParams = null,
  simulation = null
}) {
  const { trajectoryData, simulatedTrajectory, maxDistance, maxHeight } = useMemo(() => {
    const generateParabola = (distance, height) => {
      const points = [];
      const steps = 30;

      for (let i = 0; i <= steps; i++) {
        const x = (distance * i) / steps;
        // Parabola: y = (4h/d²) * x * (d - x)
        const y = (4 * height / (distance * distance)) * x * (distance - x);
        points.push({ distance: Math.round(x), height: Math.round(y * 10) / 10 });
      }

      return points;
    };

    const trajectories = [];
    let maxDist = 0;
    let maxHt = 0;

    shotGroups.forEach(group => {
      const shots = group.shots.filter(s => s.included);
      if (shots.length === 0) return;

      const avgDistance = shots.reduce((sum, s) => {
        const d = distanceType === 'carry' ? s.metrics.carry : s.metrics.total;
        return sum + (d || 0);
      }, 0) / shots.length;

      const avgHeight = shots.reduce((sum, s) => sum + (s.metrics.height || 0), 0) / shots.length;

      if (avgDistance > 0 && avgHeight > 0) {
        maxDist = Math.max(maxDist, avgDistance);
        maxHt = Math.max(maxHt, avgHeight);

        trajectories.push({
          id: group.id,
          name: group.name,
          color: group.color,
          points: generateParabola(avgDistance, avgHeight),
          avgDistance,
          avgHeight
        });
      }
    });

    // Generate simulated trajectory if in simulation mode
    let simTrajectory = null;
    if (simulationMode && trajectories.length > 0) {
      // Use 6DOF simulation result if available (real physics)
      if (simulation?.trajectory_2d?.length > 0) {
        const simPoints = simulation.trajectory_2d.map(p => ({
          distance: p.x,
          height: p.y,
        }));
        const simCarry = simulation.summary?.carry || 0;
        const simApex = simulation.summary?.apex_yards || 0;

        maxDist = Math.max(maxDist, simCarry);
        maxHt = Math.max(maxHt, simApex);

        simTrajectory = {
          id: 'simulated',
          name: 'Simulated (6DOF)',
          color: '#10b981',
          points: simPoints,
          avgDistance: simCarry,
          avgHeight: simApex,
          isSimulated: true
        };
      } else if (simulatedParams) {
        // Fallback: parabola approximation if no 6DOF result yet
        const avgDist = trajectories.reduce((sum, t) => sum + t.avgDistance, 0) / trajectories.length;
        const avgHt = trajectories.reduce((sum, t) => sum + t.avgHeight, 0) / trajectories.length;
        const avgLaunchAngle = shotGroups.reduce((sum, group) => {
          const shots = group.shots.filter(s => s.included);
          const launchSum = shots.reduce((s, shot) => s + (shot.metrics.launchAngle || 12), 0);
          return sum + (shots.length > 0 ? launchSum / shots.length : 12);
        }, 0) / shotGroups.length;

        const launchDelta = (simulatedParams.launch_angle || avgLaunchAngle) - avgLaunchAngle;
        const simDist = avgDist + launchDelta * 2;
        const simHt = Math.max(avgHt + launchDelta * 3, avgHt * 0.5);

        maxDist = Math.max(maxDist, simDist);
        maxHt = Math.max(maxHt, simHt);

        simTrajectory = {
          id: 'simulated',
          name: 'Simulated',
          color: '#10b981',
          points: generateParabola(simDist, simHt),
          avgDistance: simDist,
          avgHeight: simHt,
          isSimulated: true
        };
      }
    }

    return {
      trajectoryData: trajectories,
      simulatedTrajectory: simTrajectory,
      maxDistance: maxDist,
      maxHeight: maxHt
    };
  }, [shotGroups, showAverage, distanceType, simulationMode, simulatedParams, simulation]);

  if (trajectoryData.length === 0) {
    return (
      <div className="flex items-center justify-center h-96 text-text-muted">
        No trajectory data available
      </div>
    );
  }

  // Merge all trajectory points into single dataset
  const mergedData = useMemo(() => {
    const merged = [];
    const allTrajectories = [...trajectoryData];
    if (simulatedTrajectory) {
      allTrajectories.push(simulatedTrajectory);
    }

    const maxPoints = Math.max(...allTrajectories.map(t => t.points.length));

    for (let i = 0; i < maxPoints; i++) {
      const point = { distance: 0 };
      allTrajectories.forEach(traj => {
        if (i < traj.points.length) {
          point.distance = traj.points[i].distance;
          point[traj.id] = traj.points[i].height;
        }
      });
      merged.push(point);
    }

    return merged;
  }, [trajectoryData, simulatedTrajectory]);

  return (
    <div className="w-full h-72 bg-background rounded-lg">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={mergedData} margin={{ top: 10, right: 10, bottom: 35, left: 45 }}>
          <CartesianGrid
            strokeDasharray="3 3"
            stroke="#333333"
            strokeOpacity={0.3}
          />

          <XAxis
            dataKey="distance"
            type="number"
            domain={[0, Math.ceil(maxDistance / 10) * 10]}
            stroke="#9ca3af"
            tick={{ fill: '#9ca3af', fontSize: 12 }}
            label={{
              value: 'Distance (yards)',
              position: 'insideBottom',
              offset: -15,
              fill: '#9ca3af',
              fontSize: 13
            }}
          />

          <YAxis
            type="number"
            domain={[0, Math.ceil(maxHeight / 5) * 5]}
            stroke="#9ca3af"
            tick={{ fill: '#9ca3af', fontSize: 12 }}
            label={{
              value: 'Height (yards)',
              angle: -90,
              position: 'insideLeft',
              fill: '#9ca3af',
              fontSize: 13
            }}
          />

          <Tooltip
            contentStyle={{
              backgroundColor: 'rgba(20, 20, 20, 0.95)',
              border: '1px solid #333',
              borderRadius: '4px',
              color: '#f4f4f5',
              fontSize: '12px',
              padding: '8px'
            }}
            formatter={(value, name) => {
              const traj = trajectoryData.find(t => t.id === name);
              return [`${Math.round(value)} yds`, traj?.name || name];
            }}
          />

          <Legend
            wrapperStyle={{ color: '#9ca3af' }}
            formatter={(value) => {
              const traj = trajectoryData.find(t => t.id === value);
              return traj?.name || value;
            }}
          />

          {/* Ground reference line */}
          <ReferenceLine y={0} stroke="#666" strokeWidth={2} />

          {/* Actual trajectory lines */}
          {trajectoryData.map(traj => (
            <Line
              key={traj.id}
              type="monotone"
              dataKey={traj.id}
              stroke={traj.color}
              strokeWidth={2}
              strokeOpacity={simulationMode ? 0.3 : 1}
              dot={false}
              activeDot={{ r: 6 }}
            />
          ))}

          {/* Simulated trajectory line */}
          {simulatedTrajectory && (
            <Line
              key="simulated"
              type="monotone"
              dataKey="simulated"
              stroke="#10b981"
              strokeWidth={2.5}
              strokeOpacity={0.9}
              dot={false}
              activeDot={{ r: 6 }}
            />
          )}
        </LineChart>
      </ResponsiveContainer>

      {/* Distance markers below chart */}
      <div className="flex flex-wrap gap-3 text-xs text-text-muted mt-2 px-4 sm:px-12 justify-center sm:justify-start">
        {trajectoryData.map(traj => (
          <div key={traj.id} className="flex items-center gap-2 whitespace-nowrap" style={{ opacity: simulationMode ? 0.5 : 1 }}>
            <div
              className="w-3 h-3 rounded-full flex-shrink-0"
              style={{ backgroundColor: traj.color }}
            />
            <span>{traj.name}: {Math.round(traj.avgDistance)} yds</span>
            <span className="text-text-secondary hidden sm:inline">| Apex: {Math.round(traj.avgHeight)} yds</span>
            <span className="text-text-secondary sm:hidden">({Math.round(traj.avgHeight)} yds apex)</span>
          </div>
        ))}
        {simulatedTrajectory && (
          <div key="simulated" className="flex items-center gap-2 whitespace-nowrap font-semibold text-success">
            <div
              className="w-3 h-3 rounded-full flex-shrink-0"
              style={{ backgroundColor: '#10b981' }}
            />
            <span>Simulated: {Math.round(simulatedTrajectory.avgDistance)} yds</span>
            <span className="text-success/70 hidden sm:inline">| Apex: {Math.round(simulatedTrajectory.avgHeight)} yds</span>
            <span className="text-success/70 sm:hidden">({Math.round(simulatedTrajectory.avgHeight)} yds apex)</span>
          </div>
        )}
      </div>
    </div>
  );
});

TrajectoryChart.propTypes = {
  shotGroups: PropTypes.array.isRequired,
  selectedShots: PropTypes.array,
  showAverage: PropTypes.bool,
  distanceType: PropTypes.oneOf(['carry', 'total']),
  simulationMode: PropTypes.bool,
  simulatedParams: PropTypes.object
};

export default TrajectoryChart;
